import React from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import { Table } from 'react-bootstrap'

import GoogleIcon from '../../../../components/icon/GoogleIcon'
import { formatMoney, formatQuantity, toSafeNumber } from '../../../../helpers/utils'
import { UI_TEXT_EM_DASH } from '../../../../constants/uiText'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import { getRefundLineStatusUI } from '../../../constants/refundLineStatus'
import {
  REFUND_PRODUCTS_TABLE_HEADERS,
  getRefundLineCondition,
  getRefundLineListKey
} from '../../../helpers/refundApprovalUtils'
import { getPrimaryBarcode } from '../../helpers/refundScanModalUtils'
import {
  UI_COLOR_BORDER_HAIRLINE,
  UI_COLOR_BORDER_MEDIUM,
  UI_COLOR_DANGER_OUTLINE,
  UI_COLOR_DANGER_SURFACE,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PRIMARY_PRESS,
  UI_COLOR_SURFACE_DISABLED_BTN,
  UI_COLOR_SURFACE_SUCCESS_ROW,
  UI_COLOR_SURFACE_SUCCESS_ROW_ALT,
  UI_COLOR_TEXT_DISABLED,
  UI_COLOR_TEXT_INK,
  UI_COLOR_WHITE
} from '../../../../constants/uiColors'

const RefundApprovalProductsTable = ({
  lines,
  showLineActions,
  loading,
  onEditLine,
  onRejectLine
}) => (
  <Box
    sx={{
      overflowX: 'auto',
      mb: 2,
      '& .refund-products-table': {
        minWidth: 1180,
        mb: 0,
        backgroundColor: UI_COLOR_WHITE
      },
      '& .refund-products-table th': {
        height: 52,
        px: 1.5,
        py: 1,
        verticalAlign: 'middle',
        fontSize: 12,
        lineHeight: 1.25,
        whiteSpace: 'nowrap'
      },
      '& .refund-products-table td': {
        height: 58,
        px: 1.5,
        py: 1.25,
        verticalAlign: 'middle',
        borderColor: UI_COLOR_BORDER_HAIRLINE,
        color: UI_COLOR_TEXT_INK,
        fontSize: 12.5
      },
      '& .refund-products-table td:nth-of-type(2)': {
        minWidth: 205,
        whiteSpace: 'normal'
      },
      '& .refund-products-table tbody tr.refund-products-table__processed > td': {
        backgroundColor: `${UI_COLOR_SURFACE_SUCCESS_ROW} !important`
      },
      '& .refund-products-table tbody tr.refund-products-table__processed:hover > td': {
        backgroundColor: `${UI_COLOR_SURFACE_SUCCESS_ROW_ALT} !important`
      },
      '& .refund-products-table th:last-of-type, & .refund-products-table td:last-of-type': {
        textAlign: 'center'
      }
    }}
  >
    <Table hover responsive className="refund-products-table">
      <thead className="table-header-gray-mui">
        <tr>
          {REFUND_PRODUCTS_TABLE_HEADERS.map((header) => (
            <th key={header}>{header}</th>
          ))}
          {showLineActions && <th>{REFUND_APPROVAL_TEXT.actionColumn}</th>}
        </tr>
      </thead>
      <tbody>
        {lines.length > 0 ? (
          lines.map((line, index) => {
            const originalQty = toSafeNumber(line.original_quantity ?? line.quantity)
            const verifiedQty = line.verified_quantity
            const lineTotal = originalQty * toSafeNumber(line.unit_price)
            const lineStatusUI = getRefundLineStatusUI(line)
            const isCounted = line.line_status !== 'pending'
            const primaryBarcode = getPrimaryBarcode(line.product_barcode)
            const hasLineUid =
              line.refund_item_uid !== undefined &&
              line.refund_item_uid !== null &&
              line.refund_item_uid !== ''

            return (
              <tr
                key={getRefundLineListKey(line, index)}
                className={isCounted ? 'refund-products-table__processed' : undefined}
              >
                <td>{line.product_db_ref || UI_TEXT_EM_DASH}</td>
                <td>{line.product_name || UI_TEXT_EM_DASH}</td>
                <td>{primaryBarcode || UI_TEXT_EM_DASH}</td>
                <td>{line.price_unit || line.unit_of_sale || REFUND_APPROVAL_TEXT.defaultUnit}</td>
                <td>{line.motive || UI_TEXT_EM_DASH}</td>
                <td>{getRefundLineCondition(line)}</td>
                <td>{formatQuantity(originalQty)}</td>
                <td>{verifiedQty != null ? formatQuantity(verifiedQty) : UI_TEXT_EM_DASH}</td>
                <td>{formatMoney(toSafeNumber(line.unit_price))}</td>
                <td>{formatMoney(lineTotal)}</td>
                <td>
                  <span className={`order-status ${lineStatusUI.className}`}>
                    {lineStatusUI.label}
                  </span>
                </td>
                {showLineActions && (
                  <td>
                    {isCounted ? (
                      <Tooltip title={REFUND_APPROVAL_TEXT.editCount}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`${REFUND_APPROVAL_TEXT.editCountAriaPrefix} ${line.product_name || line.product_db_ref || REFUND_APPROVAL_TEXT.productFallback}`}
                            onClick={() => onEditLine(line.refund_item_uid)}
                            disabled={loading || !hasLineUid}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: 1,
                              backgroundColor: UI_COLOR_PRIMARY_ALT,
                              color: UI_COLOR_WHITE,
                              '&:hover': { backgroundColor: UI_COLOR_PRIMARY_PRESS },
                              '&.Mui-disabled': {
                                backgroundColor: UI_COLOR_SURFACE_DISABLED_BTN,
                                color: UI_COLOR_WHITE
                              }
                            }}
                          >
                            <GoogleIcon name="edit" size={17} color={UI_COLOR_WHITE} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={REFUND_APPROVAL_TEXT.rejectLineTooltip}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`${REFUND_APPROVAL_TEXT.rejectAriaPrefix} ${line.product_name || line.product_db_ref || REFUND_APPROVAL_TEXT.productFallback}`}
                            disabled={loading || !hasLineUid}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: 1,
                              border: `1px solid ${UI_COLOR_DANGER_OUTLINE}`,
                              color: UI_COLOR_DANGER_OUTLINE,
                              backgroundColor: UI_COLOR_WHITE,
                              '&:hover': { backgroundColor: UI_COLOR_DANGER_SURFACE },
                              '&.Mui-disabled': {
                                borderColor: UI_COLOR_BORDER_MEDIUM,
                                color: UI_COLOR_TEXT_DISABLED
                              }
                            }}
                            onClick={() => onRejectLine(line)}
                          >
                            <GoogleIcon name="close" size={18} color="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </td>
                )}
              </tr>
            )
          })
        ) : (
          <tr>
            <td colSpan={showLineActions ? 12 : 11}>{REFUND_APPROVAL_TEXT.emptyProducts}</td>
          </tr>
        )}
      </tbody>
    </Table>
  </Box>
)

export default RefundApprovalProductsTable
